import React from 'react';
import { ImageGroupField as ImageGroupFieldType } from '../../types/optionTypes';

interface ImageGroupFieldProps {
    field: ImageGroupFieldType;
    value: any;
    onChange: (value: any) => void;
}

export const ImageGroupField: React.FC<ImageGroupFieldProps> = ({ field, value, onChange }) => {
    const handleImageSelect = (optionValue: string) => {
        if (field.multiple) {
            const currentValues = Array.isArray(value) ? value : [];
            if (currentValues.includes(optionValue)) {
                onChange(currentValues.filter(v => v !== optionValue));
            } else {
                onChange([...currentValues, optionValue]);
            }
        } else {
            onChange(optionValue);
        }
    };

    const isSelected = (optionValue: string) => {
        if (field.multiple) {
            return Array.isArray(value) && value.includes(optionValue);
        }
        return value === optionValue;
    };

    const layoutClass = field.layOut === 'col-2' ? 'grid-cols-2' : 'grid-cols-1';
    const displayType = field.disp_type || 'square';

    return (
        <div className="w-full">
            {field.label && (
                <label className="block text-sm font-medium text-gray-800 mb-3">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            <div className={`grid ${layoutClass} gap-3`}>
                {field.values.map((option) => {
                    const selected = isSelected(option.value);
                    const imageUrl = option.image || option.thumb;

                    return (
                        <button
                            key={option.tempId}
                            type="button"
                            onClick={() => handleImageSelect(option.value)}
                            className={`
                                relative border-2 rounded-lg overflow-hidden transition-all
                                ${selected
                                    ? 'border-blue-600 ring-2 ring-blue-200 bg-blue-50'
                                    : 'border-gray-300 hover:border-gray-400 bg-white'
                                }
                                ${displayType === 'square' ? 'aspect-square' : ''}
                            `}
                        >
                            {imageUrl ? (
                                <>
                                    <div className="w-full h-full flex items-center justify-center p-2">
                                        <img
                                            src={imageUrl}
                                            alt={option.label || option.value}
                                            className="max-w-full max-h-full object-contain"
                                        />
                                    </div>
                                    {option.label && !field.hideOptionLabel && (
                                        <div className={`
                                            absolute bottom-0 left-0 right-0 p-2 text-xs font-medium text-center
                                            ${selected ? 'bg-blue-600 text-white' : 'bg-gray-900 bg-opacity-75 text-white'}
                                        `}>
                                            {option.label}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className={`
                                    w-full h-full flex items-center justify-center p-3 text-sm font-medium text-center
                                    ${selected ? 'text-blue-700' : 'text-gray-700'}
                                `}>
                                    {option.label || option.value}
                                </div>
                            )}

                            {selected && (
                                <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full p-1">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {field.description && (
                <div className="mt-2 text-xs text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: field.description }} />
            )}
        </div>
    );
};


